'use client';

import React from 'react';
import { usePathname } from 'next/navigation';
import { Mail } from 'lucide-react';

export default function SidebarNewsletter() {
    const pathname = usePathname();
    const [email, setEmail] = React.useState("");
    const [status, setStatus] = React.useState<"idle" | "loading" | "success" | "error">("idle");
    const [message, setMessage] = React.useState("");
    const [mounted, setMounted] = React.useState(false);

    React.useEffect(() => {
        setMounted(true);
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) return;

        setStatus("loading");
        setMessage("");

        const source = `${pathname} Sidebar`;

        try {
            const res = await fetch("/api/subscribe", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, source }),
            });

            const data = await res.json();

            if (!res.ok) throw new Error(data.error || "Failed to subscribe");

            setStatus("success");
            setMessage(data.message);
            setEmail("");
        } catch (error: any) {
            setStatus("error");
            setMessage(error.message);
        }
    };

    return (
        <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-8">
            <div className="flex items-center gap-3 mb-4">
                <div className="bg-[#00ff9d]/10 p-2 rounded-lg text-[#00ff9d]">
                    <Mail size={20} />
                </div>
                <h3 className="font-bold text-white text-lg">Join Smart Savers</h3>
            </div>

            <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                Get exclusive coupons and money-saving tips delivered to your inbox weekly.
            </p>

            <form onSubmit={handleSubmit} className="space-y-3" suppressHydrationWarning>
                <div className="relative">
                    {mounted ? (
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="your@email.com"
                            disabled={status === "loading" || status === "success"}
                            className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#00ff9d]/50 focus:ring-1 focus:ring-[#00ff9d]/20 transition-all disabled:opacity-50"
                            required
                            data-lpignore="true"
                        />
                    ) : (
                        <div className="h-[46px] w-full bg-black/40 border border-white/10 rounded-lg" />
                    )}
                </div>

                <button
                    type="submit"
                    disabled={status === "loading" || status === "success"}
                    className={`w-full font-bold px-4 py-3 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 ${status === "success"
                        ? "bg-[#00ff9d] text-black hover:bg-[#00cc7d]"
                        : "bg-[#00ff9d] text-black hover:bg-[#00cc7d] hover:shadow-[0_0_15px_rgba(0,255,157,0.3)]"
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                    {status === "loading" ? "Subscribing..." : status === "success" ? "Subscribed! ✓" : "Subscribe Now"}
                </button>

                {message && (
                    <p className={`text-xs text-center ${status === "error" ? "text-red-400" : "text-[#00ff9d]"}`}>
                        {message}
                    </p>
                )}

                <p className="text-xs text-gray-400 text-center mt-3">
                    No spam. Unsubscribe anytime.
                </p>
            </form>
        </div>
    );
}
